import { Checkbox, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TextFieldComponent from "components/input/TextFieldComponent";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import { formatCurrency } from "utils";
import { memo, useCallback, useMemo } from "react";
import generateUniqueId from "util/generate_id";
import { REMARK_MAX_LENGTH } from "constants/format";
import { EstQuoteDetailTire } from "features/car_model/types";
import { getRoundedValue } from "features/car_model/api/carModelApi";

interface ResultTableProps {
  mainHeader: DataHeaderType[];
  subHeader: DataHeaderType[];
  rounding: number;
  quoteDetailTire: EstQuoteDetailTire[];
  updateQuotationTire: (key: keyof EstQuoteDetailTire, index: number, value: any) => void;
  detailData?: boolean;
  /** Corresponding wheel final_price: confirmTotalPrice = tire confirm_total_price + wheelFinalPrice, then getRoundedValue */
  wheelFinalPrice?: number;
}

// Memoized header component
interface TableHeaderProps {
  mainHeader: DataHeaderType[];
  subHeader: DataHeaderType[];
}

const TableHeader = memo<TableHeaderProps>(({ mainHeader, subHeader }) => (
  <TableHead>
    <TableRow>
      {mainHeader.map((item, index) => (
        <TableCellComponent {...item} key={generateUniqueId(index)} type="header" />
      ))}
    </TableRow>
    <TableRowComponent data={subHeader} type="header" />
  </TableHead>
));

TableHeader.displayName = "TableHeader";

// Memoized tire row component
interface TireRowProps {
  item: EstQuoteDetailTire;
  index: number;
  rounding: number;
  onCheckedChange: (checked: boolean) => void;
  onRemarksChange: (value: string) => void;
  detailData?: boolean;
  wheelFinalPrice?: number;
}

const TireRow = memo<TireRowProps>(
  ({ item, index, rounding, onCheckedChange, onRemarksChange, detailData = false, wheelFinalPrice = 0 }) => {
    // Memoized background color calculation
    const backgroundColor = useMemo(
      () => (index % 2 !== 0 ? "var(--offwhite-200)" : "var(--white)"),
      [index],
    );

    // confirmTotalPrice = tire confirm_total_price + corresponding wheel final_price, then rounded
    const formattedPrices = useMemo(
      () => {
        const tireConfirm = item.confirm_total_price ?? 0;
        const totalBeforeRound = tireConfirm + (wheelFinalPrice ?? 0);
        return {
          wageCostPrice: formatCurrency(item.wage_cost_price ?? 0),
          punctureProtectionPrice: formatCurrency(item.puncture_protection_price ?? 0),
          cbTirePrice: formatCurrency(item.cb_tire_price ?? 0),
          normalPrice: formatCurrency(item.nomal_price),
          discountedSalePrice: item.discounted_sale_price
            ? formatCurrency(item.discounted_sale_price)
            : "",
          wageCostInPrice: formatCurrency(item.wage_cost_in_price),
          punctureProtectionInPrice: item.puncture_protection_in_price
            ? formatCurrency(item.puncture_protection_in_price)
            : "",
          confirmTotalPrice: formatCurrency(getRoundedValue(totalBeforeRound, rounding)),
        };
      },
      [item, rounding, wheelFinalPrice],
    );

    // Memoized discount value
    const discountValue = useMemo(
      () => item.special_discount ?? item.fixed_discount ?? 0,
      [item.special_discount, item.fixed_discount],
    );

    const handleCheckboxChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onCheckedChange(e.target.checked);
      },
      [onCheckedChange],
    );

    const rowSx = useMemo(
      () => ({
        backgroundColor,
        height: "60px",
      }),
      [backgroundColor],
    );

    const cellSx = useMemo(
      () => ({
        border: "1px solid var(--offwhite-500)",
      }),
      [],
    );

    return (
      <>
        <TableRow sx={rowSx}>
          {!detailData && (
            <TableCellComponent rowSpan={2} sx={cellSx}>
              <Checkbox
                color="secondary"
                inputProps={{
                  "aria-label": "select all desserts",
                }}
                checked={item.checked}
                onChange={handleCheckboxChange}
              />
            </TableCellComponent>
          )}
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {item.maker_name}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {item.tire_loin_speed_mark}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {item.quantity}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {discountValue}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "right", ...cellSx }}>
            {formattedPrices.wageCostPrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "right", ...cellSx }}>
            {formattedPrices.punctureProtectionPrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "right", ...cellSx }}>
            {formattedPrices.cbTirePrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "left", ...cellSx, py: 1 }} rowSpan={2}>
            {detailData ? (
              item.remarks
            ) : (
              <TextFieldComponent
                multiline
                sx={{ height: "100%", py: 0, my: 0 }}
                sxRoot={{ height: "100%", alignItems: "start" }}
                sxInput={{
                  padding: "0px 0px 0px 0px",
                  height: "100%",
                  fontSize: { xs: "16px", md: "18px", xl: "20px" },
                }}
                sxLabelProps={{ alignItems: "flex-start" }}
                value={item.remarks}
                onChangeText={onRemarksChange}
                validateOption={{
                  maxLength: { message: "", value: REMARK_MAX_LENGTH },
                }}
              />
            )}
          </TableCellComponent>
        </TableRow>
        <TableRow sx={rowSx}>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {item.item_name}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {item.tire_label}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {formattedPrices.normalPrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "center", ...cellSx }}>
            {formattedPrices.discountedSalePrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "right", ...cellSx }}>
            {formattedPrices.wageCostInPrice}
          </TableCellComponent>
          <TableCellComponent sx={{ textAlign: "right", ...cellSx }}>
            {formattedPrices.punctureProtectionInPrice}
          </TableCellComponent>
          <TableCellComponent
            sx={{ textAlign: "right", color: "var(--red-800)", fontWeight: "bold" }}
          >
            {formattedPrices.confirmTotalPrice}
          </TableCellComponent>
        </TableRow>
      </>
    );
  },
);

TireRow.displayName = "TireRow";

/**
 * QuotationResultTableTire component renders a detailed result table for tire quotes.
 * @param {object} props - The component props.
 * @param {DataHeaderType[]} props.mainHeader - The main header configuration for the table.
 * @param {DataHeaderType[]} props.subHeader - The sub header configuration for the table.
 * @param {number} props.rounding - The rounding option for price calculations.
 * @param {EstQuoteDetailTire[]} props.quoteDetailTire - The array of tire quote details to display.
 * @param {(key: keyof EstQuoteDetailTire, index: number, value: any) => void} props.updateQuotationTire - Callback to update a specific field in a tire quote detail.
 */
function QuotationResultTableTire({
  mainHeader,
  subHeader,
  rounding,
  quoteDetailTire,
  updateQuotationTire,
  detailData = false,
  wheelFinalPrice = 0,
}: ResultTableProps) {
  // Memoized handlers for each tire item
  const handleCheckedChange = useCallback(
    (index: number) => (checked: boolean) => {
      updateQuotationTire("checked", index, checked);
    },
    [updateQuotationTire],
  );

  const handleRemarksChange = useCallback(
    (index: number) => (value: string) => {
      updateQuotationTire("remarks", index, value);
    },
    [updateQuotationTire],
  );

  // Memoized table container styles
  const tableContainerSx = useMemo(
    () => ({
      boxShadow: "none",
      minWidth: "1600px",
      width: "100%",
    }),
    [],
  );

  return (
    <TableContainer component={Table} sx={tableContainerSx}>
      <TableHeader mainHeader={mainHeader} subHeader={subHeader} />
      <TableBody>
        {quoteDetailTire.map((item, index) => (
          <TireRow
            key={item.tire_item_cd}
            item={item}
            index={index}
            rounding={rounding}
            onCheckedChange={handleCheckedChange(index)}
            onRemarksChange={handleRemarksChange(index)}
            detailData={detailData}
            wheelFinalPrice={wheelFinalPrice}
          />
        ))}
      </TableBody>
    </TableContainer>
  );
}

export default memo(QuotationResultTableTire);
